import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import type { ILatestSync } from 'interfaces/schedule/ILatestSync';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { serializeError } from 'serialize-error';
import { IStrikeZoneDefinition } from '../interfaces/session/IStrikeZoneDefinition';
import { IApiGameLevel, IApiLeague, IApiScheduleResponse, IOrganization } from '../interfaces/team/IApiScheduleResponse';
import { ContextualService } from '../services/data-services/ContextualService';
import { LogService } from '../services/data-services/LogService';
import { ScheduleService } from '../services/data-services/ScheduleService';
import { ContextStore } from './ContextStore';
import { NotificationStore } from './NotificationStore';

dayjs.extend(utc);
dayjs.extend(relativeTime);
export class ContextualDataStore {
  private contextualService: ContextualService;
  private scheduleService: ScheduleService;
  private logService: LogService;
  private contextStore: ContextStore;
  private notificationStore: NotificationStore;

  constructor(
    contextualService: ContextualService,
    scheduleService: ScheduleService,
    logService: LogService,
    contextStore: ContextStore,
    notificationStore: NotificationStore
  ) {
    this.contextualService = contextualService;
    this.scheduleService = scheduleService;
    this.scheduledGames = [];
    this.leagues = [];
    this.gameLevels = [];
    this.owningOrganizations = [];
    this.strikeZones = [];
    this.logService = logService;
    this.contextStore = contextStore;
    this.notificationStore = notificationStore;
    makeObservable(this);
  }

  @observable scheduledGames: IApiScheduleResponse[];

  @observable leagues: IApiLeague[];

  @observable gameLevels: IApiGameLevel[];

  @observable owningOrganizations: IOrganization[];

  @observable strikeZones: IStrikeZoneDefinition[];

  @observable latestSync: ILatestSync = {} as ILatestSync;

  @observable isHomePageGameDialogVisible = false;

  @observable isGameDialogVisible = false;

  @observable isBpDialogVisible = false;

  @observable isUpdating = false;

  @observable isSyncBarVisible = true;

  // TODO: get rid off defaultStrikeZone from contextualData - remove from UI
  @observable defaultStrikeZone: IStrikeZoneDefinition | undefined;

  @observable umpires: IUmpire[] = [];

  @action setLatestSync = async () => {
    const sync = await this.contextualService.GetLatestSyncAll();
    if (sync) {
      this.latestSync = sync;
    }
  };

  @action setIsSyncBarVisible = (visible: boolean) => {
    this.isSyncBarVisible = visible;
  };

  @action refreshCurrentDayWithoutSync = async () => {
    try {
      const gamesPromise = this.fetchScheduledLocalGames();
      const leaguesPromise = this.fetchLeagues();
      const levelsPromise = this.fetchGameLevels();
      const orgsPromise = this.fetchOwningOrganizations();
      await Promise.all([gamesPromise, leaguesPromise, levelsPromise, orgsPromise]);
    } catch (err) {
      console.log(err);
      console.log(serializeError(err));
      this.logService.logError(err, { function: 'ContextualDataStore@refreshCurrentDayWithoutSync' });
      this.notificationStore.addNotification({
        id: 'contextual-data-store-refresh-current-day-error',
        message: 'Refresh failed',
        variant: 'error',
        removable: true,
        onClose: () => {
          this.notificationStore.removeNotification('contextual-data-store-refresh-current-day-error');
        },
      });
    }
  };

  @action fetchScheduledLocalGames = async () => {
    try {
      const games = await this.scheduleService.GetGamesScheduledLocalForToday();
      this.sortScheduledGames(games);
      // @ts-ignore
      runInAction(() => (this.scheduledGames = games));
    } catch (err) {
      this.logService.logError(err, { function: 'ContextualDataStore@fetchScheduledLocalGames' });
      this.notificationStore.addNotification({
        id: 'contextual-data-store-fetch-local-games-error',
        message: 'Error fetching local games',
        variant: 'error',
        removable: true,
        onClose: () => {
          this.notificationStore.removeNotification('contextual-data-store-fetch-local-games-error');
        },
      });

      return false;
    }
    return true;
  };

  @action syncAllAndGetLatestSync = async () => {
    try {
      this.isUpdating = true;
      await this.contextualService.SyncAll();
      const gamesPromise = this.fetchScheduledGames();
      const leaguesPromise = this.fetchLeagues();
      const levelsPromise = this.fetchGameLevels();
      const orgsPromise = this.fetchOwningOrganizations();
      const [games, leagues, levels, orgs] = await Promise.all([gamesPromise, leaguesPromise, levelsPromise, orgsPromise]);
      if (games && leagues && levels && orgs) {
        await this.setLatestSync();
      }
    } catch (err) {
      console.log(err);
      console.log(serializeError(err));
      this.logService.logError(err, { function: 'ContextualDataStore@syncAllAndGetLatestSync' });
      this.notificationStore.addNotification({
        id: 'contextual-data-store-sync-all-error',
        message: 'Sync failed',
        variant: 'error',
        removable: true,
        onClose: () => {
          this.notificationStore.removeNotification('contextual-data-store-sync-all-error');
        },
      });
    } finally {
      this.isUpdating = false;
    }
  };

  @action fetchScheduledGames = async () => {
    try {
      const games = await this.scheduleService.GetGamesScheduledForToday();
      this.sortScheduledGames(games);
      runInAction(() => {
        this.scheduledGames = games;
      });
    } catch (err) {
      this.logService.logError(err, { function: 'ContextualDataStore@fetchScheduledGames' });
      this.notificationStore.addNotification({
        id: 'contextual-data-store-fetch-scheduled-games-error',
        message: 'Error fetching games',
        variant: 'error',
        removable: true,
        onClose: () => {
          this.notificationStore.removeNotification('contextual-data-store-fetch-scheduled-games-error');
        },
      });
      return false;
    }
    return true;
  };

  @action fetchLeagues = async () => {
    try {
      const leagues = await this.contextualService.GetLeagues();
      this.leagues = leagues;
    } catch (err) {
      this.logService.logError(err, { function: 'ContextualDataStore@fetchLeagues' });
      this.notificationStore.addNotification({
        id: 'contextual-data-store-fetch-leagues-error',
        message: 'Error fetching leagues',
        variant: 'error',
        removable: true,
        onClose: () => {
          this.notificationStore.removeNotification('contextual-data-store-fetch-leagues-error');
        },
      });
      return false;
    }
    return true;
  };

  @action fetchGameLevels = async () => {
    try {
      const gameLevels = await this.contextualService.GetGameLevels();
      this.gameLevels = gameLevels;
    } catch (err) {
      this.logService.logError(err, { function: 'ContextualDataStore@fetchGameLevels' });
      this.notificationStore.addNotification({
        id: 'contextual-data-store-fetch-game-levels-error',
        message: 'Error fetching game levels',
        variant: 'error',
        removable: true,
        onClose: () => {
          this.notificationStore.removeNotification('contextual-data-store-fetch-game-levels-error');
        },
      });
      return false;
    }
    return true;
  };

  @action fetchOwningOrganizations = async () => {
    try {
      this.contextStore.ensureActiveFieldPresent();
      const owningOrganizations = await this.contextualService.GetOwningOrganizations(this.contextStore.activeField!.fieldId);
      this.owningOrganizations = owningOrganizations;
    } catch (err) {
      this.logService.logError(err, { function: 'ContextualDataStore@fetchOwningOrganizations' });
      this.notificationStore.addNotification({
        id: 'contextual-data-store-fetch-owning-organizations-error',
        message: 'Error fetching owning organizations',
        variant: 'error',
        removable: true,
        onClose: () => {
          this.notificationStore.removeNotification('contextual-data-store-fetch-owning-organizations-error');
        },
      });
      return false;
    }
    return true;
  };

  @action setIsGameDialogVisible(isVisible: boolean) {
    this.isGameDialogVisible = isVisible;
  }

  @action setIsHomePageGameDialogVisible(isVisible: boolean) {
    this.isHomePageGameDialogVisible = isVisible;
  }

  @action setIsBpDialogVisible(isVisible: boolean) {
    this.isBpDialogVisible = isVisible;
  }

  getLeagueShortNameById = (id: string | undefined): string => {
    let foundShortName = '';
    this.leagues.forEach((league) => {
      if (league.id === id) {
        foundShortName = league.shortName ? league.shortName : '';
      }
    });
    return foundShortName;
  };

  getNextGameStartingIn = () => {
    const getStartingIn = (stringDate: string | undefined) => {
      if (!stringDate) {
        return '';
      }
      const date = dayjs.utc(stringDate);
      const now = dayjs.utc();

      if (date.isBefore(now)) {
        return `${date.from(now)}`;
      }
      return `${now.to(date)}`;
    };

    return getStartingIn(this.getNextGame()?.armingTime).toLowerCase();
  };

  getNextGame = () => {
    const upcomingGames = this.scheduledGames
      .filter((s) => dayjs.utc(s.armingTime).isAfter(dayjs.utc()))
      .filter((s) => s.numberOfSessions === 0);

    if (upcomingGames.length === 0) {
      return;
    }
    const getAbsoluteTimeDiff = (time: string) => {
      return Math.abs(dayjs.utc().diff(dayjs.utc(time), 'second'));
    };

    const nextGame = upcomingGames.reduce((prev, current) =>
      getAbsoluteTimeDiff(prev.armingTime) < getAbsoluteTimeDiff(current.armingTime) ? prev : current
    );

    return nextGame;
  };

  fetchUmpires = async () => {
    try {
      const umpires = await this.contextualService.GetUmpires();
      this.umpires = umpires;
    } catch (err) {
      this.logService.logError(err, { function: 'ContextualDataStore@fetchUmpires' });
      this.notificationStore.addNotification({
        id: 'contextual-data-store-fetch-umpires-error',
        message: 'Error fetching umpires',
        variant: 'error',
        removable: true,
        onClose: () => {
          this.notificationStore.removeNotification('contextual-data-store-fetch-umpires-error');
        },
      });
    }
  };

  private sortScheduledGames = (games: IApiScheduleResponse[]) => {
    games.sort((a, b) => {
      const aStarted = a.numberOfSessions > 0 ? 1 : 0;
      const bStarted = b.numberOfSessions > 0 ? 1 : 0;
      if (aStarted !== bStarted) {
        return aStarted - bStarted;
      }
      return dayjs(a.localExpectedTime).isAfter(dayjs(b.localExpectedTime)) ? 1 : -1;
    });
  };
}
